import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MarketingNav, MarketingFooter } from '@/components/MarketingFooter';
import { CoachGalleryCarousel } from '@/components/LandingExtras';
import { TestimonialsWall } from '@/components/TestimonialsWall';
import { Star, Heart, ArrowRight } from 'lucide-react';

/**
 * /customers — Phase 92 (site audit fixes D5.1 + D2.*).
 *
 * Dedicated customers page with its own title/meta (previously the route
 * fell through to the homepage). Shows the real coach gallery plus the
 * testimonials, and links to the public coach map.
 */
export default function CustomersPage() {
    return (
        <div className="min-h-screen bg-[#f4f7fb]">
            <Seo
                title="SplashPad Customers — Real Swim Coaches & Schools"
                description="The independent swim instructors running their schools on SplashPad: 32+ verified coaches on the public map and a 100+ coach community behind them."
                canonical="https://splashpad.pro/customers"
            />
            <MarketingNav />

            <section className="py-14 lg:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="mx-auto mb-10 max-w-3xl text-center">
                        <Badge className="mb-3 border-[#00d4ff]/40 bg-[#00d4ff]/15 text-[#0a1628] hover:bg-[#00d4ff]/15">
                            <Heart className="mr-1 h-3 w-3 text-[#ff6b35]" /> Real coaches using SplashPad
                        </Badge>
                        <h1 className="font-display text-4xl font-bold text-[#0a1628] sm:text-5xl">
                            Coaches building swim schools they love
                        </h1>
                        <p className="mt-3 text-[#1a3a5c]">
                            32+ verified coaches on the public map, backed by a 100+ instructor community.
                            Every profile below is a real coach page you can visit.
                        </p>
                    </div>

                    <CoachGalleryCarousel />

                    <div className="mt-6 text-center">
                        <Link to="/#find-coach" className="inline-flex items-center gap-1 text-sm font-medium text-[#00d4ff] hover:underline" data-testid="customers-map-link">
                            Browse every coach on the world map <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
            </section>

            <section className="bg-white py-14 lg:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <h2 className="mb-10 text-center font-display text-3xl font-bold text-[#0a1628]">Loved by independent coaches</h2>
                    {/* Phase 93 — real in-app coach reviews first, placeholders behind. */}
                    <TestimonialsWall />

                    <div className="mt-12 text-center">
                        <Link
                            to="/signup?role=instructor"
                            className="sd-cta sd-focus inline-flex items-center rounded-xl px-6 py-3 text-base font-semibold"
                            data-testid="customers-page-cta"
                        >
                            Join them — Start Free <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
